import React, { useEffect, useMemo, useState } from 'react';
import Drawer, { FooterSpacer } from '../Drawer';
import Button from '../Button';
import Field from '../Field';
import { useInvoices } from '../../data/useInvoices';
import { useSettings } from '../../data/useSettings';
import { useMoneyFormat } from '../../context/LocaleContext';
import { todayISO } from '../../domain/clock';
import {
  DISPLAY_STYLES, includePreviousOptions, zeroBalanceOptions,
  invoiceCanContinue, newInvoiceForm, seedFooterNote,
} from '../../domain/invoice';
import { monthRange, familyPickerOrder } from '../../domain/familyAccount';
import { familyDisplayName } from '../../domain/people';
import { invoiceEmailOptions, invoiceEmailWord } from '../../domain/invoiceEmail';
import s from './NewInvoiceDrawer.module.css';
import { DateInput } from '../RegionInputs';

// Generating invoices: which families, over what period, and how the lines read.

// The current month, on the UTC calendar grid: a local midnight converted to
// UTC lands a day early east of Greenwich. `monthRange` builds both ends with
// utcDate(), which never converts, and the form's other defaults are
// `newInvoiceForm` (src/domain/invoice.js).
const openingForm = () => {
  const today = todayISO();
  const { start, end } = monthRange(today);
  return newInvoiceForm({ today, periodStart: start, periodEnd: end });
};

export default function NewInvoiceDrawer({ open, onClose, families }) {
  const { requestGeneration } = useInvoices();
  // The Families list by name, in the core's `labelOrder`, as Family Accounts,
  // Add Transaction and both phones' pickers order it.
  const pickable = useMemo(
    () => familyPickerOrder(families.map((x) => familyDisplayName(x))).map((i) => families[i]),
    [families],
  );
  const formatMoney = useMoneyFormat();
  // Business Settings → Accounts & Invoices → Invoice Footer Text is the
  // *default* for this run's footer note, not a second footer: MMS has one
  // footer on the document, and the note typed here is about this invoice.
  const { settings } = useSettings();
  const defaultFooter = settings.invoicing.footerText || '';
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [f, setF] = useState(openingForm);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  // Seeded when the drawer opens rather than in useState's initialiser: the
  // studio document arrives on a snapshot listener and is still empty on the
  // first render. Only ever fills an empty note, so it cannot overwrite one the
  // teacher has typed.
  useEffect(() => {
    if (!open || !defaultFooter) return;
    setF((p) => (p.footerNote ? p : { ...p, footerNote: seedFooterNote(p.footerNote, defaultFooter) }));
  }, [open, defaultFooter]);
  const close = () => { setStep(0); setError(null); onClose?.(); };

  const run = async () => {
    setSaving(true); setError(null);
    try {
      await requestGeneration(f);
      // Generation is asynchronous, so the drawer closes on a successful
      // request. The row appears at once as "Creating" and fills in when the
      // function finishes, as in MMS.
      close();
    } catch (err) { setError(err?.message || String(err)); }
    finally { setSaving(false); }
  };

  return (
    <Drawer open={open} onClose={close} title="New Invoice"
      steps={['Invoice Details', 'Preferences']} currentStep={step}
      footer={
        <>
          <Button variant="secondary" onClick={close}>Cancel</Button>
          <FooterSpacer />
          {step > 0 ? <Button variant="secondary" onClick={() => setStep(0)}>Previous</Button> : null}
          {step === 0
            ? <Button onClick={() => setStep(1)} disabled={!invoiceCanContinue(f)}>Next</Button>
            : <Button onClick={run} loading={saving}>Create</Button>}
        </>
      }>
      {error ? (
        <div className={s.error}>{error}</div>
      ) : null}

      {step === 0 ? (
        <>
          <h3 className={s.question}>Who do you want to create an invoice for?</h3>
          <Field label="Families">
            <select multiple className={s.families} value={f.familyIds}
                    onChange={(e) => set('familyIds', Array.from(e.target.selectedOptions, (o) => o.value))}>
              {pickable.map((x) => (
                <option key={x.id} value={x.id}>{familyDisplayName(x)}</option>
              ))}
            </select>
          </Field>
          <div className={s.narrow}>
            <Field label="Invoice Date" hint="Appears at the top of the invoice">
              <DateInput className={s.input} value={f.invoiceDate} onChange={(e) => set('invoiceDate', e.target.value)} />
            </Field>
          </div>
          <div className={s.groupLabel}>What do you want to include?</div>
          {includePreviousOptions().map(({ value, label }) => (
            <label key={String(value)} className={s.option}>
              <input type="radio" name="incprev" className={s.optionRadio}
                     checked={f.includePreviousBalance === value}
                     onChange={() => set('includePreviousBalance', value)} />
              <span className={s.optionLabel}>{label}</span>
            </label>
          ))}
          <div className={s.pair}>
            <Field label="Period start"><DateInput className={s.input} value={f.periodStart} onChange={(e) => set('periodStart', e.target.value)} /></Field>
            <Field label="Period end"><DateInput className={s.input} value={f.periodEnd} onChange={(e) => set('periodEnd', e.target.value)} /></Field>
          </div>
        </>
      ) : (
        <>
          <div className={s.groupLabelTight}>Display Style</div>
          {Object.values(DISPLAY_STYLES).map((d) => (
            <label key={d.id} className={s.option}>
              <input type="radio" name="ds" className={s.optionRadio}
                     checked={f.displayStyle === d.id}
                     onChange={() => set('displayStyle', d.id)} />
              <span>
                <span className={s.optionLabel}>{d.label}</span>
                <span className={s.optionHelp}>{d.help}</span>
              </span>
            </label>
          ))}
          <div className={s.groupLabel}>If a family has a {formatMoney(0)} or negative balance</div>
          {zeroBalanceOptions().map(({ id, label }) => (
            <label key={id} className={s.optionCentred}>
              <input type="radio" name="zero" checked={f.zeroBalanceBehaviour === id}
                     onChange={() => set('zeroBalanceBehaviour', id)} />
              <span className={s.optionLabel}>{label}</span>
            </label>
          ))}
          <div className={s.section}>
            <Field label="Footer Note" hint="Shows at the bottom of the invoice">
              <textarea rows={3} className={s.input} value={f.footerNote} onChange={(e) => set('footerNote', e.target.value)} />
            </Field>
          </div>
          {/* MMS's step two ends on Email: "No, create but don't email", its
              default, or "Yes, email invoices immediately" (SPEC §3.4, #622).
              Yes rides on the request, and the function emails each invoice
              with its PDF once it has written it
              (functions/invoicing/email.js). */}
          <div className={s.groupLabel}>{invoiceEmailWord('emailLabel')}</div>
          {invoiceEmailOptions().map(({ value, label }) => (
            <label key={String(value)} className={s.optionCentred}>
              <input type="radio" name="email" checked={(f.emailInvoices === true) === value}
                     onChange={() => set('emailInvoices', value)} />
              <span className={s.optionLabel}>{label}</span>
            </label>
          ))}
          {f.emailInvoices ? <div className={s.optionHelp}>{invoiceEmailWord('emailYesHint')}</div> : null}
        </>
      )}
    </Drawer>
  );
}
